import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, AlertTriangle, Download, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { serpAnalysisApi, type KwPoolItem } from "@/api/serp-analysis";

const PER_PAGE = 50;

function exportCsv(rows: KwPoolItem[]) {
  const headers = [
    "keyword", "translation", "monthly_searches",
    "cat1", "cat2", "cat3", "search_intent", "buying_stage",
  ];
  const lines = [
    headers.join(","),
    ...rows.map(r =>
      headers.map(h => {
        const val = String(r[h as keyof KwPoolItem] ?? "");
        return val.includes(",") ? `"${val}"` : val;
      }).join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "keyword-map.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function KwMapTab() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [cat1Filter, setCat1Filter] = useState("");
  const [cat2Filter, setCat2Filter] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ["kw-pool", search, cat1Filter, cat2Filter, page],
    queryFn: async () => {
      const res = await serpAnalysisApi.getKwPool({
        search: search || undefined,
        cat1: cat1Filter && cat1Filter !== "__all__" ? cat1Filter : undefined,
        cat2: cat2Filter && cat2Filter !== "__all__" ? cat2Filter : undefined,
        page,
        per_page: PER_PAGE,
      });
      // Server returns { success, data: [...], total } at top level.
      // apiFetch returns the full JSON, so res itself matches KwPoolResponse.
      return res as unknown as import("@/api/serp-analysis").KwPoolResponse;
    },
  });

  // Collect distinct cat1/cat2 values from current page for filter dropdowns
  const cat1Options = useMemo(() => {
    const seen = new Set<string>();
    for (const r of data?.data ?? []) {
      if (r.cat1) seen.add(r.cat1);
    }
    return Array.from(seen).sort();
  }, [data]);

  const cat2Options = useMemo(() => {
    const seen = new Set<string>();
    for (const r of data?.data ?? []) {
      if (r.cat2) seen.add(r.cat2);
    }
    return Array.from(seen).sort();
  }, [data]);

  const totalPages = Math.ceil((data?.total ?? 0) / PER_PAGE);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleCat1Change = (val: string) => {
    setCat1Filter(val);
    setCat2Filter("");
    setPage(1);
  };

  const handleCat2Change = (val: string) => {
    setCat2Filter(val);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-1 flex-1 min-w-[200px]">
          <Input
            placeholder="搜尋關鍵字 / Search keywords..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            className="flex-1"
          />
          <Button variant="outline" size="icon" onClick={handleSearch}>
            <Search className="h-4 w-4" />
          </Button>
        </div>

        <Select value={cat1Filter || "__all__"} onValueChange={handleCat1Change}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Cat1" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Cat1</SelectItem>
            {cat1Options.map(v => (
              <SelectItem key={v} value={v}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={cat2Filter || "__all__"} onValueChange={handleCat2Change}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Cat2" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Cat2</SelectItem>
            {cat2Options.map(v => (
              <SelectItem key={v} value={v}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={() => data?.data && exportCsv(data.data)}
          disabled={!data?.data?.length}
        >
          <Download className="h-4 w-4 mr-1" />
          匯出 CSV
        </Button>
      </div>

      {/* Stats */}
      {data && (
        <p className="text-sm text-muted-foreground">
          共 {data.total.toLocaleString()} 筆關鍵字
          {search && ` (搜尋: "${search}")`}
        </p>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="py-8 text-center text-red-500">
          <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">{(error as Error).message}</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>關鍵字 Keyword</TableHead>
                <TableHead>中文翻譯</TableHead>
                <TableHead className="text-right">月搜尋量 MSV</TableHead>
                <TableHead>Cat1</TableHead>
                <TableHead>Cat2</TableHead>
                <TableHead>Cat3</TableHead>
                <TableHead>搜尋意圖</TableHead>
                <TableHead>購買階段</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    無符合條件的關鍵字
                  </TableCell>
                </TableRow>
              ) : (
                data?.data?.map((row, i) => (
                  <TableRow key={`${row.keyword}-${i}`}>
                    <TableCell className="font-mono text-sm">{row.keyword}</TableCell>
                    <TableCell className="text-sm">{row.translation ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.monthly_searches != null
                        ? row.monthly_searches.toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell className="text-sm">{row.cat1 ?? "—"}</TableCell>
                    <TableCell className="text-sm">{row.cat2 ?? "—"}</TableCell>
                    <TableCell className="text-sm">{row.cat3 ?? "—"}</TableCell>
                    <TableCell className="text-sm">{row.search_intent ?? "—"}</TableCell>
                    <TableCell className="text-sm">{row.buying_stage ?? "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            上一頁
          </Button>
          <span className="text-sm text-muted-foreground">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            下一頁
          </Button>
        </div>
      )}
    </div>
  );
}
